import { cloneElement, useContext, useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ZillowMapContext from '@zillow/zillow-map-control/dist/ZillowMapContext';

type BottomCircleStyle = 'default' | 'plain';

interface CustomMapDotProps {
  lat: number;
  lng: number;
  icon: React.ReactElement;
  backgroundColor?: string;
  iconColor?: string;
  bottomCircleColor?: string;
  bottomCircleStyle?: BottomCircleStyle;
  zIndex?: number;
  popoverTitle?: string;
  popoverContent?: string | React.ReactNode;
  popoverHeaderAction?: React.ReactNode;
  onOpen?: () => void;
}

interface MarkerIconProps {
  icon: React.ReactElement;
  backgroundColor?: string;
  width?: number;
  height?: number;
  iconColor?: string;
  bottomCircleColor?: string;
  bottomCircleStyle?: BottomCircleStyle;
}

const BottomCircle = ({ color, style = 'default' }: { color: string; style?: BottomCircleStyle }) => {
  if (style === 'plain') {
    return <circle cx="25" cy="46" r="5" fill={color} stroke="#111116" strokeWidth={1} />;
  }
  return <circle cx="25" cy="46" r="7" fill={color} stroke="white" strokeWidth={3} />;
};

// Shared marker shape component
const MarkerShape = ({
  backgroundColor,
  bottomCircleColor,
  bottomCircleStyle,
}: {
  backgroundColor: string;
  bottomCircleColor?: string;
  bottomCircleStyle?: BottomCircleStyle;
}) => (
  <>
    {bottomCircleColor && <BottomCircle color={bottomCircleColor} style={bottomCircleStyle} />}

    {/* Marker body */}
    <rect
      x="7.5"
      y="8"
      width="35"
      height="30"
      rx="15"
      ry="15"
      fill={backgroundColor}
      stroke="#111116"
      strokeWidth={1}
    />
    <rect x="22" y="37" width="6" height="2" rx="1" fill={backgroundColor} stroke="none" />

    {/* Centered pointy tail*/}
    <polygon points="25,42 22,38 28,38" fill={backgroundColor} stroke="none" />
    {/* Left edge */}
    <line x1="22" y1="38" x2="25" y2="42" stroke="#111116" strokeWidth={1} strokeLinejoin="round" />
    {/* Right edge */}
    <line x1="28" y1="38" x2="25" y2="42" stroke="#111116" strokeWidth={1} strokeLinejoin="round" />
  </>
);

export const MarkerIcon = ({
  icon,
  backgroundColor = '#111116',
  width = 50,
  height = 53,
  iconColor = 'white',
  bottomCircleColor,
  bottomCircleStyle,
}: MarkerIconProps) => {
  const iconWithIconColor = cloneElement(icon, {
    style: { color: iconColor, fill: iconColor },
  });

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 55 58">
      <MarkerShape
        backgroundColor={backgroundColor}
        bottomCircleColor={bottomCircleColor}
        bottomCircleStyle={bottomCircleStyle}
      />

      <foreignObject x="17" y="15" width="16" height="16">
        <div
          style={{
            width: '16px',
            height: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {iconWithIconColor}
        </div>
      </foreignObject>
    </svg>
  );
};

const createMarkerIcon = (
  iconElement: React.ReactElement,
  backgroundColor: string,
  iconColor: string,
  bottomCircleColor?: string,
  bottomCircleStyle?: BottomCircleStyle,
): string => {
  // Extract the icon's SVG content
  const iconMarkup = renderToStaticMarkup(iconElement);
  const match = iconMarkup.match(/<svg[^>]*>(.*?)<\/svg>/s);
  const iconContent = match ? match[1] : '';

  // Build the SVG string directly without dangerouslySetInnerHTML
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="50" height="53" viewBox="0 0 55 58">
      ${renderToStaticMarkup(<MarkerShape backgroundColor={backgroundColor} bottomCircleColor={bottomCircleColor} bottomCircleStyle={bottomCircleStyle} />)}
      <g transform="translate(25, 23)">
        <g transform="translate(-9, -10) scale(0.55)" fill="${iconColor}" color="${iconColor}">
          ${iconContent}
        </g>
      </g>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const createPopoverHeaderElement = (title: string, headerAction?: React.ReactNode): HTMLDivElement => {
  const container = document.createElement('div');
  container.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 240px;
  `;

  const titleEl = document.createElement('div');
  titleEl.style.cssText = `
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #111116;
    flex: 1;
  `;
  titleEl.textContent = title;
  container.appendChild(titleEl);

  if (headerAction) {
    const actionEl = document.createElement('div');
    actionEl.style.cssText = `
      display: flex;
      align-items: center;
      flex-shrink: 0;
    `;
    actionEl.innerHTML = renderToStaticMarkup(<>{headerAction}</>);
    container.appendChild(actionEl);
  }

  return container;
};

const createPopoverContent = (content: string | React.ReactNode, hasHeader: boolean): string => {
  const bodyContent = typeof content === 'string' ? content : renderToStaticMarkup(<>{content}</>);

  const dividerHtml = hasHeader
    ? `<div style="
        height: 1px;
        background-color: #e5e5e5;
        margin: 0 -12px 12px -12px;
      "></div>`
    : '';

  return `
    <style>
      .gm-style-iw-tc { display: none !important; }
      .gm-style-iw-d { overflow: visible !important; padding: 12px !important; }
      .gm-style-iw-ch { padding-top: 12px !important; padding-bottom: 0 !important; }
    </style>
    <div style="
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      min-width: 240px;
      max-width: 320px;
      font-size: 14px;
      color: #111116;
      line-height: 1.5;
      margin-inline-start: -12px;
      margin-block-start: -16px;
      min-width: calc(100% + 12px);
    ">
      ${dividerHtml}
      ${bodyContent}
    </div>
  `;
};

export const IconMapDot = ({
  lat,
  lng,
  icon,
  backgroundColor = '#111116',
  iconColor = 'white',
  bottomCircleColor,
  bottomCircleStyle,
  zIndex,
  popoverTitle,
  popoverContent,
  popoverHeaderAction,
  onOpen,
}: CustomMapDotProps) => {
  const { gmap } = useContext<{ gmap: google.maps.Map }>(ZillowMapContext);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  useEffect(() => {
    if (!gmap || !window.google?.maps?.Data) return;

    const point = new google.maps.LatLng(lat, lng);

    // Remove existing marker if it exists
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Remove existing info window if it exists
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }

    const marker = new google.maps.Marker({
      position: point,
      ...(zIndex != null && { zIndex }),
      icon: {
        url: createMarkerIcon(icon, backgroundColor, iconColor, bottomCircleColor, bottomCircleStyle),
        anchor: new google.maps.Point(25, bottomCircleColor ? 58 : 50), // Anchor at the tip of the tail (or bottom circle if present)
      },
    });

    marker.setMap(gmap);
    markerRef.current = marker;

    // Create info window if popoverContent is provided
    if (popoverContent) {
      const contentString = createPopoverContent(popoverContent, !!popoverTitle);

      const infoWindowOptions: google.maps.InfoWindowOptions = {
        content: contentString,
      };

      if (popoverTitle) {
        infoWindowOptions.headerContent = createPopoverHeaderElement(popoverTitle, popoverHeaderAction);
      }

      const infoWindow = new google.maps.InfoWindow(infoWindowOptions);

      infoWindowRef.current = infoWindow;

      // Add click listener to open info window
      marker.addListener('click', () => {
        // Call onOpen callback if provided
        onOpen?.();

        // Timeout to sent the opening to the end of the event loop
        // so it does not close immediately with the document click listener
        // DO NOT REMOVE
        setTimeout(() => {
          infoWindow.open({
            anchor: marker,
            map: gmap,
          });
        }, 0);
      });

      // Close info window when clicking outside (on the map)
      // Only close the infoWindow if the click is NOT inside the infoWindow element
      function handleDocumentClick(event: MouseEvent) {
        const infoWindowContainer = document.querySelector('.gm-style-iw');
        if (infoWindowContainer && infoWindowContainer.contains(event.target as Node)) {
          return; // Click was inside the InfoWindow; do not close
        }
        infoWindow.close();
      }

      document.addEventListener('click', handleDocumentClick);

      // Store the listener for cleanup
      return () => {
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
        document.removeEventListener('click', handleDocumentClick);
      };
    }

    // Cleanup function to remove the marker and info window when component unmounts
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gmap, lat, lng, backgroundColor, iconColor, bottomCircleColor, bottomCircleStyle, zIndex, popoverTitle]);

  // Update info window content when popoverContent changes
  useEffect(() => {
    if (infoWindowRef.current && popoverContent) {
      const contentString = createPopoverContent(popoverContent, !!popoverTitle);
      infoWindowRef.current.setContent(contentString);
    }
  }, [popoverContent, popoverTitle]);

  return null;
};
